import { useCallback, useMemo } from "react";
import type { Promotion } from "@b2bportal/hsbc-promotions-api";
import {
  CardCriteriaEnum,
  CriteriaEnum,
  type PayWithCardBin,
} from "@b2bportal/hsbc-wallet-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import type { UsePremierDestinationPromotionReturn } from "@hsbc/portal";
import { getCardByBin } from "@hsbc/utilities";

export type CardEarnList = Record<
  string,
  { cardNames: string[]; rate: string }
>;

interface UseCardEarnListParams {
  promotions: UsePremierDestinationPromotionReturn["promotions"];
}

interface UseCardEarnListReturn {
  hotelEarnList: CardEarnList;
  flightEarnList: CardEarnList;
}

export const useCardEarnList = ({
  promotions,
}: UseCardEarnListParams): UseCardEarnListReturn => {
  const { t } = useI18nContext();
  const { hotels: hotelPromotion, flights: flightPromotion } = promotions;

  const getEarnList = useCallback(
    (promos: Promotion[]) => {
      const earnList = {};
      for (const promo of promos) {
        const { bin: cardBins = [] } = promo?.applicability.criterias.find(
          (c) =>
            c.Criteria === CriteriaEnum.CardCriteria &&
            c.CardCriteria === CardCriteriaEnum.PayWithCardBin
        ) as PayWithCardBin;

        // Remove duplicate card names for different bins, but same name
        const cardNames = Array.from(
          new Set(
            cardBins
              ?.map((bin) => getCardByBin(bin, false)?.name)
              .filter((name): name is string => name != null) ?? []
          )
        );

        if (promo?.awards.multiplier != null && cardNames?.length > 0) {
          earnList[promo?.awards.multiplier] = {
            cardNames,
            rate: t("premierDestinations.earnDialog.milesEarnRate", {
              miles: promo?.awards.multiplier,
            }),
          };
        }
      }

      return earnList;
    },
    [t]
  );

  const hotelEarnList = useMemo(
    () => (hotelPromotion.length > 0 ? getEarnList(hotelPromotion) : {}),
    [hotelPromotion, getEarnList]
  );

  // We don't need cars here, as flights and cars are the same for now
  const flightEarnList = useMemo(
    () => (flightPromotion.length > 0 ? getEarnList(flightPromotion) : {}),
    [flightPromotion, getEarnList]
  );

  return {
    hotelEarnList,
    flightEarnList,
  };
};
